import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import {
  Box,
  Card,
  CardContent,
  Grid,
  IconButton,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { ToolTip } from 'components/Tooltip/ToolTip';
import React, { FC } from 'react';
import strings from '../../../../../../../common/Translation/Translate';
import { VenueConfigSectionAction } from '../../../VenueConfigSectionAction';
import { venueConfigDeleteIconProps } from '../../../venueConfigTableStyles';
import { VenueConfigTimeField } from './VenueConfigTimeField';
import { TimeslotMinimumRevenueRow } from './timeslotFormUtils';

type TimeslotMinimumRevenueCardsProps = {
  rows: TimeslotMinimumRevenueRow[];
  errors?: Record<number, string>;
  onChange: (index: number, field: keyof TimeslotMinimumRevenueRow, value: string) => void;
  onBlur: (index: number) => void;
  onDelete: (index: number) => void;
  onAdd: () => void;
};

const TimeSlotIcon: FC = () => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        width: 56,
        height: 56,
        borderRadius: 1,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'action.hover',
        color: 'primary.main',
        border: `1px solid ${theme.palette.divider}`,
      }}>
      <AccessTimeIcon fontSize="medium" />
    </Box>
  );
};

export const TimeslotMinimumRevenueCards: FC<TimeslotMinimumRevenueCardsProps> = ({
  rows,
  errors = {},
  onChange,
  onBlur,
  onDelete,
  onAdd,
}) => (
  <>
    <Box sx={{ mb: 1.5 }}>
      <VenueConfigSectionAction label={strings.venueConfig_timeslot_revenue_add} onClick={onAdd} />
      {rows.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
          {strings.venueConfig_mapping_timeslot_revenue_empty}
        </Typography>
      )}
    </Box>
    {rows.length > 0 && (
      <Grid container spacing={2}>
        {rows.map((row, index) => {
          const error = errors[index];
          const hasError = Boolean(error);
          const label =
            row.startTime && row.endTime ? `${row.startTime} - ${row.endTime}` : strings.timeslot_text;

          return (
            <Grid item xs={12} sm={6} md={4} key={row.id ?? `revenue-${index}`}>
              <Card variant="outlined" sx={{ height: '100%', borderColor: hasError ? 'error.main' : 'divider' }}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Box display="flex" alignItems="flex-start" gap={1.5}>
                    <TimeSlotIcon />
                    <Box flex={1} minWidth={0}>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                        <Typography variant="subtitle1" fontWeight={600} noWrap title={label}>
                          {label}
                        </Typography>
                        <ToolTip title={strings.deleteText}>
                          <IconButton
                            size="small"
                            onClick={() => onDelete(index)}
                            aria-label="delete minimum revenue">
                            <DeleteOutlineOutlinedIcon {...venueConfigDeleteIconProps} />
                          </IconButton>
                        </ToolTip>
                      </Box>
                      <Box display="flex" gap={1} mt={1.5}>
                        <VenueConfigTimeField
                          label={strings.venueConfig_timeslot_start}
                          value={row.startTime}
                          onChange={v => onChange(index, 'startTime', v)}
                          onBlur={() => onBlur(index)}
                          hasError={hasError}
                        />
                        <VenueConfigTimeField
                          label={strings.venueConfig_timeslot_end}
                          value={row.endTime}
                          onChange={v => onChange(index, 'endTime', v)}
                          onBlur={() => onBlur(index)}
                          hasError={hasError}
                        />
                      </Box>
                      <TextField
                        label={strings.minimum_revenue_text}
                        size="small"
                        type="number"
                        fullWidth
                        value={row.minimumRevenue}
                        onChange={e => onChange(index, 'minimumRevenue', e.target.value)}
                        onBlur={() => onBlur(index)}
                        error={hasError}
                        inputProps={{ min: 0, step: 'any' }}
                        sx={{ mt: 1.5 }}
                      />
                      {hasError && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                          {error}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    )}
  </>
);
